import React, { useCallback, useMemo, useState } from 'react';
import { ReactFlow, Background, BackgroundVariant, Node, Edge, Position, Handle, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Step } from '../api/models/Workflows';
import { createWorkflow } from '../api/thunks/workflows';
import { useAppSelector } from '../store/hooks';
import { Mail, Cpu, DatabaseIcon, Clock, Diamond, PlugIcon, CurlyBraces, Zap } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { setNotification } from '../store/notifications-reducer';
import { useNavigate } from 'react-router-dom';

interface WorkflowPreviewComponentProps {
    workflowJson: string;
    onWorkflowCreated?: () => void;
}

// Matches the API format from gen/server/models
interface WorkflowData {
    name: string;
    enabled: boolean;
    steps: Array<{
        stepType: string;
        order: number;
        dataSourceStep?: {
            ticker: string;
            subType: string;
        };
        llmStep?: {
            model: string;
            reasoningEffort?: 'low' | 'medium' | 'high' | null;
            prompt: string;
            useSystemPrompt: boolean;
            structuredOutput?: string;
        };
        emailStep?: {
            subject: string;
            autoTransformBody: boolean;
        };
        decisionStep?: {
            isSmartDecisionStep: boolean;
            model: string;
            smartDecisionStepQuestion: string;
            targetAttribute: string;
            targetValue: string;
            operator: string;
            attributeType: string;
        };
        scheduleStep?: {
            schedule: string;
        };
        jsonTransformationStep?: {
            jmespath: string;
        };
        apiDestinationStep?: {
            url: string;
            method: string;
            authHeaderName: string;
            authHeaderSecretName: string;
        };
    }>;
}

const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatLocalTime = (hour: number, minute: number) => {
    const now = new Date();
    now.setUTCHours(hour, minute, 0, 0);
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(now);
};

const describeCronSchedule = (schedule: string) => {
    const fields = schedule.trim().split(/\s+/);
    if (fields.length !== 5) return `Custom: ${schedule}`;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
    if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') return 'Every minute';
    if ((minute === '*/15' || minute === '0/15') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') return 'Every 15 minutes';
    if (minute === '0' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') return 'Every hour';

    const minuteNumber = Number(minute);
    const hourNumber = Number(hour);
    const isSpecificTime = Number.isInteger(minuteNumber) && minuteNumber >= 0 && minuteNumber <= 59
        && Number.isInteger(hourNumber) && hourNumber >= 0 && hourNumber <= 23;
    if (!isSpecificTime) return `Custom: ${schedule}`;

    const localTime = formatLocalTime(hourNumber, minuteNumber);
    const time = `${localTime} local`;
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') return `Every day at ${time}`;
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '1-5') return `Every weekday at ${time}`;
    if (dayOfMonth === '*' && month === '*' && /^\d$/.test(dayOfWeek) && weekdayNames[Number(dayOfWeek)]) return `Every ${weekdayNames[Number(dayOfWeek)]} at ${time}`;
    if (/^\d{1,2}$/.test(dayOfMonth) && month === '*' && dayOfWeek === '*') return `Monthly on day ${dayOfMonth} at ${time}`;
    if (/^\d{1,2}$/.test(dayOfMonth) && /^\d{1,2}$/.test(month) && dayOfWeek === '*') return `Yearly on ${month}/${dayOfMonth} at ${time}`;

    return `Custom: ${schedule}`;
};

// Mini node components for preview
const PreviewDataSourceNode = ({ data }: NodeProps<Node<{ ticker: string; dataType: string }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <DatabaseIcon className="w-4 h-4" />
            <span className="text-xs font-medium">Data Source</span>
        </div>
        <div className="text-[10px] mt-1 truncate">{data.ticker || 'Any'}</div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const PreviewLLMNode = ({ data }: NodeProps<Node<{ model: string; prompt: string }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <Cpu className="w-4 h-4" />
            <span className="text-xs font-medium">LLM</span>
        </div>
        <div className="text-[10px] mt-1 truncate">{data.model}</div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const PreviewEmailNode = ({ data }: NodeProps<Node<{ subject: string }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" />
            <span className="text-xs font-medium">Email</span>
        </div>
        <div className="text-[10px] mt-1 truncate">{data.subject}</div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const PreviewDecisionNode = ({ data }: NodeProps<Node<{ isSmartDecision: boolean }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <Diamond className="w-4 h-4" />
            <span className="text-xs font-medium">Decision</span>
        </div>
        <div className="text-[10px] mt-1">{data.isSmartDecision ? 'Smart' : 'Rule'}</div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const PreviewScheduleNode = ({ data }: NodeProps<Node<{ schedule: string }>>) => {
    const scheduleDescription = describeCronSchedule(data.schedule);

    return (
        <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
            <Handle type="target" position={Position.Left} />
            <div className="flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-xs font-medium">Schedule</span>
            </div>
            <div className="workflow-proposal__schedule text-[10px] mt-1" title={`${scheduleDescription} · Cron: ${data.schedule}`}>
                {scheduleDescription}
            </div>
            <Handle type="source" position={Position.Right} />
        </div>
    );
};

const PreviewJsonTransformNode = ({ data }: NodeProps<Node<{ jmesPath: string }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <CurlyBraces className="w-4 h-4" />
            <span className="text-xs font-medium">Transform</span>
        </div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const PreviewApiDestinationNode = ({ data }: NodeProps<Node<{ url: string; method: string }>>) => (
    <div className="workflow-proposal__node p-3 min-w-[120px] text-center">
        <Handle type="target" position={Position.Left} />
        <div className="flex items-center justify-center gap-2">
            <PlugIcon className="w-4 h-4" />
            <span className="text-xs font-medium">API</span>
        </div>
        <div className="text-[10px] mt-1 truncate">{data.method}</div>
        <Handle type="source" position={Position.Right} />
    </div>
);

const previewNodeTypes = {
    'data-source': PreviewDataSourceNode,
    'llm': PreviewLLMNode,
    'email': PreviewEmailNode,
    'decision': PreviewDecisionNode,
    'schedule': PreviewScheduleNode,
    'json-transformation': PreviewJsonTransformNode,
    'api-destination': PreviewApiDestinationNode,
};

export const WorkflowPreviewComponent: React.FC<WorkflowPreviewComponentProps> = ({ workflowJson, onWorkflowCreated }) => {
    const accessToken = useAppSelector(state => state.user.credentials?.accessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isCreating, setIsCreating] = useState(false);

    // Parse the workflow JSON - handle both valid workflows and validation errors
    const { workflowData, validationErrors } = useMemo(() => {
        try {
            const parsed = JSON.parse(workflowJson);
            // Check if this is a validation error response
            if (parsed.valid === false && parsed.errors) {
                return { workflowData: null, validationErrors: parsed.errors as string[] };
            }
            return { workflowData: parsed as WorkflowData, validationErrors: null };
        } catch (e) {
            console.error('Failed to parse workflow JSON:', e);
            return { workflowData: null, validationErrors: ['Failed to parse workflow data'] };
        }
    }, [workflowJson]);

    // Convert workflow data to ReactFlow nodes and edges
    const { nodes, edges } = useMemo(() => {
        if (!workflowData) {
            return { nodes: [], edges: [] };
        }

        const sortedSteps = [...workflowData.steps].sort((a, b) => a.order - b.order);
        const nodeList: Node[] = [];
        const edgeList: Edge[] = [];

        sortedSteps.forEach((step, index) => {
            const nodeId = `step-${index}`;
            const xPos = 50 + index * 160;
            const yPos = 50;

            let nodeData: Record<string, unknown> = {};

            switch (step.stepType) {
                case 'data-source':
                    nodeData = {
                        ticker: step.dataSourceStep?.ticker || '',
                        dataType: step.dataSourceStep?.subType || '',
                    };
                    break;
                case 'llm':
                    nodeData = {
                        model: step.llmStep?.model || '',
                        prompt: step.llmStep?.prompt || '',
                    };
                    break;
                case 'email':
                    nodeData = {
                        subject: step.emailStep?.subject || '',
                    };
                    break;
                case 'decision':
                    nodeData = {
                        isSmartDecision: step.decisionStep?.isSmartDecisionStep || false,
                    };
                    break;
                case 'schedule':
                    nodeData = {
                        schedule: step.scheduleStep?.schedule || '',
                    };
                    break;
                case 'json-transformation':
                    nodeData = {
                        jmesPath: step.jsonTransformationStep?.jmespath || '',
                    };
                    break;
                case 'api-destination':
                    nodeData = {
                        url: step.apiDestinationStep?.url || '',
                        method: step.apiDestinationStep?.method || '',
                    };
                    break;
            }

            nodeList.push({
                id: nodeId,
                type: step.stepType,
                position: { x: xPos, y: yPos },
                data: nodeData,
            });

            // Create edge to previous node
            if (index > 0) {
                edgeList.push({
                    id: `edge-${index - 1}-${index}`,
                    source: `step-${index - 1}`,
                    target: nodeId,
                    animated: true,
                    style: { stroke: '#29785e' },
                });
            }
        });

        return { nodes: nodeList, edges: edgeList };
    }, [workflowData]);

    // Convert to API format for creating workflow
    // Since the data already comes in API format from the backend, we just need to ensure proper typing
    const convertToApiSteps = useCallback((): Step[] => {
        if (!workflowData) return [];

        return workflowData.steps.map((step): Step => ({
            stepType: step.stepType as Step['stepType'],
            order: step.order,
            dataSourceStep: step.dataSourceStep ? {
                ticker: step.dataSourceStep.ticker,
                subType: step.dataSourceStep.subType as NonNullable<Step['dataSourceStep']>['subType'],
            } : undefined,
            llmStep: step.llmStep ? {
                model: step.llmStep.model as NonNullable<Step['llmStep']>['model'],
                reasoningEffort: step.llmStep.reasoningEffort || undefined,
                prompt: step.llmStep.prompt,
                useSystemPrompt: step.llmStep.useSystemPrompt,
                structuredOutput: step.llmStep.structuredOutput,
            } : undefined,
            emailStep: step.emailStep ? {
                subject: step.emailStep.subject,
                autoTransformBody: step.emailStep.autoTransformBody,
            } : undefined,
            decisionStep: step.decisionStep ? {
                isSmartDecisionStep: step.decisionStep.isSmartDecisionStep,
                model: step.decisionStep.model,
                smartDecisionStepQuestion: step.decisionStep.smartDecisionStepQuestion,
                targetAttribute: step.decisionStep.targetAttribute,
                targetValue: step.decisionStep.targetValue,
                operator: step.decisionStep.operator as NonNullable<Step['decisionStep']>['operator'],
                attributeType: step.decisionStep.attributeType as NonNullable<Step['decisionStep']>['attributeType'],
            } : undefined,
            scheduleStep: step.scheduleStep ? {
                schedule: step.scheduleStep.schedule,
            } : undefined,
            jsonTransformationStep: step.jsonTransformationStep ? {
                jmespath: step.jsonTransformationStep.jmespath,
            } : undefined,
            apiDestinationStep: step.apiDestinationStep ? {
                url: step.apiDestinationStep.url,
                method: step.apiDestinationStep.method,
                authHeaderName: step.apiDestinationStep.authHeaderName,
                authHeaderSecretName: step.apiDestinationStep.authHeaderSecretName,
                headers: {},
                body: '',
            } : undefined,
        }));
    }, [workflowData]);

    const handleCreateWorkflow = useCallback(async () => {
        if (!workflowData || !accessToken) {
            dispatch(setNotification({
                type: 'error',
                message: 'Unable to create workflow. Please log in and try again.',
            }));
            return;
        }

        setIsCreating(true);
        try {
            const steps = convertToApiSteps();
            const workflowId = await createWorkflow(accessToken, workflowData.name, steps);
            
            dispatch(setNotification({
                type: 'success',
                message: `Workflow "${workflowData.name}" created successfully!`,
            }));

            // Trigger refresh of workflows list
            if (onWorkflowCreated) {
                onWorkflowCreated();
            }

            // Navigate to workflows page with the new workflow selected
            navigate(`/workflows?id=${workflowId}`);
        } catch (error: any) {
            console.error('Failed to create workflow:', error);
            dispatch(setNotification({
                type: 'error',
                message: error.message || 'Failed to create workflow. Please try again.',
            }));
        } finally {
            setIsCreating(false);
        }
    }, [workflowData, accessToken, convertToApiSteps, dispatch, navigate, onWorkflowCreated]);

    // Show validation errors if present
    if (validationErrors && validationErrors.length > 0) {
        return (
            <div className="workflow-proposal workflow-proposal--invalid">
                <div className="workflow-proposal__header flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="workflow-proposal__title">Workflow proposal needs revision</span>
                </div>
                <div className="workflow-proposal__body">
                    <p className="workflow-proposal__note mb-3">
                        The proposal has the following issues to resolve:
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                        {validationErrors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    if (!workflowData) {
        return (
            <div className="workflow-proposal workflow-proposal--invalid workflow-proposal__body">
                Failed to parse the workflow proposal.
            </div>
        );
    }

    return (
        <div className="workflow-proposal">
            {/* Header */}
            <div className="workflow-proposal__header">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <div>
                        <span className="workflow-proposal__eyebrow">Workflow proposal</span>
                        <span className="workflow-proposal__title">{workflowData.name}</span>
                    </div>
                </div>
                <span className="workflow-proposal__step-count">{workflowData.steps.length} steps</span>
            </div>

            {/* Canvas Preview */}
            <div className="workflow-proposal__canvas">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={previewNodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.3 }}
                    proOptions={{ hideAttribution: true }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    panOnDrag
                    zoomOnScroll={false}
                    zoomOnPinch={false}
                    zoomOnDoubleClick={false}
                >
                    <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#c8d2cc" />
                </ReactFlow>
            </div>

            {/* Footer with Create Button */}
            <div className="workflow-proposal__footer">
                <div className="workflow-proposal__note">
                    Proposed only — it is not saved until you create it.
                </div>
                <button
                    onClick={handleCreateWorkflow}
                    disabled={isCreating || !accessToken}
                    className={`workflow-proposal__create
                        px-4 py-2 font-medium text-sm flex items-center gap-2
                        ${isCreating || !accessToken
                            ? 'workflow-proposal__create--disabled cursor-not-allowed'
                            : ''
                        }
                    `}
                >
                    {isCreating ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <Zap className="w-4 h-4" />
                            Create Workflow
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};
